import { Navigate, createBrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { IS_KTDS_ENABLE_NEW_UI } from '@configs/featureFlags';
import { withLogin } from '@hoc/withLogin';
import { Manager } from '../Manager';
import { ManagerLegacy } from '../Manager.legacy';
import { RouterErrorComponent } from '../common/ErrorComponent';
import { Cart } from '../common/cart';
import { PrivacyPolicyDetail } from '../common/privacyPolicy/detail';
import { ConfirmMail } from '../common/sign/confirmMail/ConfirmMail';
import { SearchPassword } from '../common/sign/searchPassword/SearchPassword';
import { SignIn } from '../common/sign/signIn/SignIn';
import { SsoLoginPage } from '../common/sign/signIn/SsoLoginPage';
import { Store } from '../common/store';
import { History as BuyApplyHistory } from '../common/store/buyApplyHistory/detail';
import { BuyApplyHistoryList } from '../common/store/buyApplyHistory/list';
import { Products } from '../common/store/products';
import { ProductsInCategory } from '../common/store/products/ProductsInCategory';
import { ProductDetail } from '../common/store/products/productDetail';
import { ManagerLanding } from './ManagerLanding';
import { ManagerLayout } from './ManagerLayout';
import { Billing } from './billing';
import { CostAnalyze } from './billing/analyze/costAnalyze/CostAnalyze';
import { BillingDashboard } from './billing/dashboard/BillingDashboard';
import { SubscriptionBillingDetail, TenantBillingDetail } from './billing/history/detail';
import { BillingList } from './billing/history/list';
import { Home } from './home/Home';
import { PageNotFound } from './pageNotFound';
import { Subscription, SubscriptionDetail, SubscriptionList } from './subscription';
import { SubscriptionLegacy } from './subscription/Subscription.legacy';
import { SubscriptionListLegacy } from './subscription/list/SubscriptionList.legacy';
import { BillingInSubscription } from './subscription/detail/billing';
import { ExtraInfo } from './subscription/detail/extraInfo/ExtraInfo';
import { History as SubscriptionHistory } from './subscription/detail/history';
import { License } from './subscription/detail/license';
import { LicenseDetail } from './subscription/detail/license/detail/LicenseDetail';
import { Summary } from './subscription/detail/summary';
import { Usage } from './subscription/detail/usage';
import { User } from './subscription/detail/user';
import { TechnicalSupportDetail } from './subscription/technicalSupport/TechnicalSupportDetail';
import { TechnicalSupportList } from './subscription/technicalSupport/TechnicalSupportList';
import { UsageRegisterDetail } from './subscription/usageRegister/detail';
import { UsageRegisterList } from './subscription/usageRegister/list';
import { SubscriptionUserDetail } from './subscription/userMgmt/detail/SubscriptionUserDetail';
import { SubscriptionUserMgmtList } from './subscription/userMgmt/list/SubscriptionUserMgmtList';
import { UserGroup } from './userGroup';
import { CompanyInfo } from './userGroup/companyInfo';
import { LicenseRetrieve } from './userGroup/licenseRetrieve/LicenseRetrieve';
import { ManagerConfig } from './userGroup/managerConfig';
import { ScimTokenDetailPage } from './userGroup/scimToken/ScimTokenDetailPage';
import { ScimTokenPage } from './userGroup/scimToken/ScimTokenPage';
import { Security } from './userGroup/security';
import { SsoProviderDetailPage } from './userGroup/ssoProvider/SsoProviderDetailPage';
import { SsoProviderPage } from './userGroup/ssoProvider/SsoProviderPage';
import { UserGroupMgmt } from './userGroup/userGroupMgmt';
import { UserGroupBatchRegister } from './userGroup/userGroupMgmt/userBatchRegister/UserGroupBatchRegister';
import { UserGroupDetail } from './userGroup/userGroupMgmt/userGroupDetail';
import { Info } from './userGroup/userGroupMgmt/userGroupDetail/info';
import { Members } from './userGroup/userGroupMgmt/userGroupDetail/members';
import { UserMgmt } from './userGroup/userMgmt';
import ActiveUsers from './userGroup/userMgmt/ActiveUsers';
import StopUsers from './userGroup/userMgmt/StopUsers';
import { UserBatchAdd } from './userGroup/userMgmt/userBatchAdd';
import { Workflow } from './workflow';
import { SubscriptionChoice } from './workflow/connectionMgmt/create';
import { ConnectionDetail } from './workflow/connectionMgmt/detail';
import { ConnectionMgmtMgmtList } from './workflow/connectionMgmt/list';
import { WorkflowCreatePage } from './workflow/workflowMgmt/createAndDetail/WorkflowCreatePage';
import { WorkflowDetail } from './workflow/workflowMgmt/detail/WorkflowDetail';
import { WorkflowEditPage } from './workflow/workflowMgmt/detail/WorkflowEditPage';
import { WorkflowExecDetailPage } from './workflow/workflowMgmt/detail/exec/WorkflowExecDetailPage';
import { WorkflowExecListPage } from './workflow/workflowMgmt/detail/exec/WorkflowExecListPage';
import { WorkflowInfo } from './workflow/workflowMgmt/detail/info/WorkflowInfo';
import { WorkflowMgmtList } from './workflow/workflowMgmt/list';

type ManagerRouterProps = {
  enabledPrivacyPolicy: boolean;
};
const WithLoginStore = withLogin(Store);

// MZP2-6364: 1780a74 ↔ develop 사이에서 UI 가 의미 있게 변경된 페이지는 KTDS 환경에서 레거시 버전을 렌더한다.
// VITE_KTDS_ENABLE_NEW_UI 는 빌드 타임에 정적 치환되므로 사용하지 않는 컴포넌트는 dead-code 로 제거된다.
// 의미 규약: `true = KTDS 신규 UI 노출 가능` (부모 Story MZP2-6362 단방향 규약 준수).
// 1780a74 시점에는 manager/:tenantId 진입 시 home 으로 리다이렉트했음 (ManagerLanding 미존재).

const createManagerRouter = ({ enabledPrivacyPolicy }: ManagerRouterProps) =>
  createBrowserRouter([
    {
      path: '/',
      element: <ManagerLayout />,
      errorElement: (
        <ErrorBoundary>
          <RouterErrorComponent text="에러가 발생했습니다." />
        </ErrorBoundary>
      ),
      children: [
        { path: 'manager/:tenantId', element: IS_KTDS_ENABLE_NEW_UI ? <ManagerLanding /> : <Navigate to="home" replace /> },
        { path: 'manager/:tenantId/login', element: <SignIn isItAdminPage /> },
        { path: 'manager/:tenantId/sso-login', element: <SsoLoginPage /> },
        {
          path: 'manager/:tenantId',
          element: IS_KTDS_ENABLE_NEW_UI ? <Manager /> : <ManagerLegacy />,
          children: [
            { path: 'home', element: <Home /> },
            {
              path: 'subscriptions/*',
              element: IS_KTDS_ENABLE_NEW_UI ? <Subscription /> : <SubscriptionLegacy />,
              children: [
                { path: 'list', element: IS_KTDS_ENABLE_NEW_UI ? <SubscriptionList /> : <SubscriptionListLegacy /> },
                {
                  path: 'list/:subscriptionId',
                  element: <SubscriptionDetail />,
                  children: [
                    { index: true, element: <Summary /> },
                    { path: 'usage', element: <Usage /> },
                    { path: 'user', element: <User /> },
                    { path: 'license', element: <License /> },
                    { path: 'license/:licenseId', element: <LicenseDetail /> },
                    { path: 'billing', element: <BillingInSubscription /> },
                    { path: 'history', element: <SubscriptionHistory /> },
                    { path: 'extra-info', element: <ExtraInfo /> },
                  ],
                },
                { path: 'user-mgmt', element: <SubscriptionUserMgmtList /> },
                { path: 'user-mgmt/:userId', element: <SubscriptionUserDetail /> },
                { path: 'connections', element: <ConnectionMgmtMgmtList /> },
                { path: 'connections/create', element: <SubscriptionChoice /> },
                { path: 'connections/:subscriptionId/connection', element: <ConnectionDetail /> },
                { path: 'usage-registers', element: <UsageRegisterList /> },
                { path: 'usage-registers/:id', element: <UsageRegisterDetail /> },
                { path: 'technical-support', element: <TechnicalSupportList /> },
                { path: 'technical-support/:technicalInquiryId', element: <TechnicalSupportDetail /> },
                { path: '*', element: <Navigate to="list" replace /> },
              ],
            },
            {
              path: 'billings/*',
              element: <Billing />,
              children: [
                { path: 'cost', element: <CostAnalyze /> },
                { path: 'dashboard', element: <BillingDashboard /> },
                { path: 'history', element: <BillingList /> },
                { path: 'history/tenant/:month', element: <TenantBillingDetail /> },
                { path: 'history/subscription/:subscriptionBillingId', element: <SubscriptionBillingDetail /> },
                { path: '*', element: <Navigate to="cost" replace /> },
              ],
            },
            {
              path: 'workflows/*',
              element: <Workflow />,
              children: [
                { path: 'management', element: <WorkflowMgmtList /> },
                { path: 'management/create', element: <WorkflowCreatePage /> },
                {
                  path: 'management/:workflowId',
                  element: <WorkflowDetail />,
                  children: [
                    { index: true, element: <WorkflowInfo /> },
                    { path: 'executions', element: <WorkflowExecListPage /> },
                    { path: 'executions/:workflowExecId', element: <WorkflowExecDetailPage /> },
                  ],
                },
                { path: 'management/:workflowId/edit', element: <WorkflowEditPage /> },
                { path: '*', element: <Navigate to="management" replace /> },
              ],
            },
            {
              path: 'usergroups/*',
              element: <UserGroup />,
              children: [
                { path: 'companyInfo', element: <CompanyInfo /> },
                { path: 'groupMgmt', element: <UserGroupMgmt /> },
                {
                  path: 'groupMgmt/:id/*',
                  element: <UserGroupDetail />,
                  children: [
                    { index: true, element: <Info /> },
                    { path: 'members', element: <Members /> },
                  ],
                },
                { path: 'groupMgmt/batch-register', element: <UserGroupBatchRegister /> },
                {
                  path: 'memberMgmt/*',
                  element: <UserMgmt />,
                  children: [
                    { path: 'active', element: <ActiveUsers /> },
                    { path: 'stop', element: <StopUsers /> },
                    { path: '*', element: <Navigate to="active" replace /> },
                  ],
                },
                { path: 'memberMgmt/batch-register', element: <UserBatchAdd /> },
                { path: 'memberMgmt/:userId/license-retrieve', element: <LicenseRetrieve /> },
                { path: 'managerMgmt', element: <ManagerConfig /> },
                { path: 'security', element: <Security /> },
                { path: 'scim-tokens', element: <ScimTokenPage /> },
                { path: 'scim-tokens/:tokenId', element: <ScimTokenDetailPage /> },
                { path: 'sso-providers', element: <SsoProviderPage /> },
                { path: 'sso-providers/:providerId', element: <SsoProviderDetailPage /> },
                { path: '*', element: <Navigate to="companyInfo" replace /> },
              ],
            },
            {
              path: 'store/*',
              element: <WithLoginStore />,
              children: [
                { path: 'softwares', element: <Products type="Software" key="Software" /> },
                { path: 'hardwares', element: <Products type="Hardware" key="Hardware" /> },
                { path: 'category/:name', element: <ProductsInCategory /> },
                { path: 'product/:id', element: <ProductDetail /> },
                { path: 'apply-histories', element: <BuyApplyHistoryList /> },
                { path: 'apply-histories/:id', element: <BuyApplyHistory /> },
                { path: 'cart', element: <Cart /> },
                { path: '*', element: <ProductsInCategory /> },
              ],
            },
            { path: 'cart', element: <Cart /> },
          ],
        },
        { path: 'confirm-mail/:email', element: <ConfirmMail /> },
        { path: 'search-password', element: <SearchPassword /> },
        ...(enabledPrivacyPolicy
          ? [
              { path: 'privacy-policy', element: <PrivacyPolicyDetail /> },
              { path: 'privacy-policy/:version', element: <PrivacyPolicyDetail /> },
            ]
          : []),
        { path: '*', element: <PageNotFound /> },
      ],
    },
  ]);

export default createManagerRouter;
